import { useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import fetchData from "../custom_functions/fetchData";
import { url } from "../utils/config";

const TABS = ["Select Vehicle", "Select Service", "Review and Submit"];
const VEHICLE_DISPLAY_LIMIT = 10;
const SECTION_HEADING_CLASS = "text-sm font-semibold uppercase tracking-wide text-green-700";
const HELPER_TEXT_CLASS = "text-sm text-gray-600 leading-relaxed";
const MUTED_TEXT_CLASS = "text-sm text-gray-500";
const BADGE_CLASS = "rounded-full px-2 py-0.5 text-xs font-semibold";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
});

const mileageFormatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
});

function formatCurrency(value) {
    const numeric = Number(value);
    return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
}

function formatMileage(value) {
    const numeric = Number(value);
    const safeValue = Number.isFinite(numeric) ? Math.max(numeric, 0) : 0;
    return `${mileageFormatter.format(safeValue)} km`;
}

function formatStatusLabel(status) {
    if (!status) return "";
    return String(status)
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

const STATUS_BADGE_CLASS = {
    active: "bg-green-100 text-green-700 border border-green-200",
    inactive: "bg-gray-100 text-gray-600 border border-gray-200",
    maintenance: "bg-amber-100 text-amber-700 border border-amber-200",
    pending: "bg-amber-100 text-amber-700 border border-amber-200",
};

function getStatusBadgeMeta(status) {
    const normalized = String(status || "").toLowerCase();
    const className = STATUS_BADGE_CLASS[normalized] || "bg-gray-100 text-gray-600 border border-gray-200";
    const labelSource = normalized || status;
    return {
        className,
        label: formatStatusLabel(labelSource),
    };
}

function normalizeVehicleForPanel(vehicle) {
    if (!vehicle) return null;
    const raw = vehicle?.raw || {};
    const make = vehicle?.make || raw?.make || raw?.vehicle_make || "";
    const model = vehicle?.model || raw?.model || raw?.vehicle_model || "";
    const yearValue = vehicle?.year || vehicle?.manufacture_year || raw?.year || raw?.manufacture_year;
    const vehicleId = vehicle?.id ?? vehicle?.car_id ?? vehicle?.vin ?? vehicle?.vehicleId;
    const titleFallback = [make, model].filter(Boolean).join(" ");
    const title = vehicle?.title || vehicle?.displayName || vehicle?.name || titleFallback || "Unnamed Vehicle";
    const subtitle = vehicle?.subtitle || vehicle?.vin || vehicle?.plate_number || vehicle?.registration_number || "VIN unavailable";

    return {
        id: vehicleId || title,
        title,
        subtitle,
        year: yearValue ? String(yearValue) : "",
        status: vehicle?.status || vehicle?.vehicle_status || vehicle?.state,
        make,
        model,
        vin: vehicle?.vin || raw?.vin || raw?.vehicle_vin || "",
        plate: vehicle?.plate || vehicle?.plate_number || raw?.plate || raw?.plate_number || "",
        mileage: vehicle?.current_odometer || raw?.current_odometer || 0,
    };
}

function normalizeServiceBundle(bundle) {
    if (!bundle) return null;
    const serviceId = bundle?.id ?? bundle?.code;
    const price = Number(bundle?.price ?? bundle?.estimatedCost ?? bundle?.cost ?? 0) || 0;

    return {
        id: serviceId,
        title: bundle?.title || bundle?.name || "Untitled Service",
        description: bundle?.description || bundle?.shortDescription || "",
        price,
        estimatedTime: bundle?.estimatedTime || 0,
        costType: bundle?.costType || "Fixed",
    };
}

function buildVehicleSummary(selectedVehicle) {
    if (!selectedVehicle) return "";
    const raw = selectedVehicle.raw || {};
    const make = raw.make || raw.vehicle_make || selectedVehicle.make;
    const model = raw.model || raw.vehicle_model || selectedVehicle.model;
    const year = raw.year || raw.manufacture_year || selectedVehicle.year;
    return [make, model, year].filter(Boolean).join(" ");
}

function QuotationRequestPanel({
    activeTab,
    onTabChange,
    vehicles = [],
    isLoading = false,
    onVehicleSelect,
    selectedVehicleId,
    serviceBundles = [],
    isServiceLoading = false,
    partList = [],
    isPartListLoading = false,
    serviceBundleList = [],
    isServiceBundleListLoading = false,
    selectedServiceIds = [],
    onServiceToggle,
    selectedPartIds = [],
    onPartToggle,
    selectedBundleIds = [],
    onBundleToggle,
    bundleMetadata = {},
    onBundleMetadataChange,
    selectedVehicle,
    selectedServices = [],
    selectedParts = [],
    selectedBundles = [],
    subtotal = 0,
    overallTotal = 0,
    taxRate = 0,
    taxAmount = 0,
    partsBaseCostTotal = 0,
    notes = "",
    onNotesChange,
    onSubmit,
    onCancel,
    isSubmitting = false,
    customServices = [],
    onAddCustomService,
    onRemoveCustomService,
    vehicleSearch = "",
    onVehicleSearchChange,
    onVehicleSearchReset,
    tireQuantity = "",
    onTireQuantityChange,
    tireNotes = "",
    onTireNotesChange,
    isMaintenance = false,
    onIsMaintenanceChange,
}) {
    const [customServiceName, setCustomServiceName] = useState("");
    const [serviceSearch, setServiceSearch] = useState("");
    const [tireBrands, setTireBrands] = useState([]);

    // Fetch tire brands from API
    useEffect(() => {
        fetchData(
            `${url}/fleet_tires`,
            (error) => {
                console.error("Failed to load tire brands:", error);
                // Set default brands on error
                setTireBrands([{ tireBrand: "NO SPECIFIC BRAND" }]);
            },
            (response) => {
                const brands = response?.data || [];
                // Add "NO SPECIFIC BRAND" option if not already present
                const hasNoSpecificBrand = brands.some(b => b.tireBrand === "NO SPECIFIC BRAND");
                if (!hasNoSpecificBrand) {
                    brands.push({ tireBrand: "NO SPECIFIC BRAND" });
                }
                setTireBrands(brands);
            },
            false
        );
    }, []);

    const vehicleItems = useMemo(() => {
        if (!Array.isArray(vehicles)) return [];
        return vehicles.map(normalizeVehicleForPanel).filter(Boolean);
    }, [vehicles]);

    const hasVehicles = vehicleItems.length > 0;
    const isVehicleSearchActive = Boolean(vehicleSearch);
    const visibleVehicles = vehicleItems.slice(0, VEHICLE_DISPLAY_LIMIT);
    const hasVisibleVehicles = visibleVehicles.length > 0;

    function handleSelectVehicle(vehicleId) {
        if (onVehicleSelect) {
            onVehicleSelect(vehicleId);
        }
        if (onTabChange) {
            onTabChange("Select Service");
        }
    }

    function validateTireBundles() {
        const tireBundles = selectedBundles?.filter((bundle) => {
            return /tire.*mount.*and.*balance/i.test(bundle.title || "");
        }) || [];

        const missingFields = [];
        
        tireBundles.forEach((bundle) => {
            const metadata = bundleMetadata[bundle.id] || {};
            const bundleName = bundle.title || "Tire bundle";
            
            if (!metadata.tireBrand) {
                missingFields.push(`${bundleName}: Tire Brand is required`);
            }
            if (!metadata.tireChangeReason) {
                missingFields.push(`${bundleName}: Reason for Tire Change is required`);
            }
            if (!metadata.tireType) {
                missingFields.push(`${bundleName}: Tire Type is required`);
            }
        });

        return missingFields;
    }

    function handleTabChange(tab) {
        if (tab === "Review and Submit") {
            const missingFields = validateTireBundles();
            if (missingFields.length > 0) {
                toast.error(
                    <div>
                        <div className="font-semibold mb-2">Please complete required tire information:</div>
                        <ul className="list-disc pl-5 space-y-1">
                            {missingFields.map((field, idx) => (
                                <li key={idx} className="text-sm">{field}</li>
                            ))}
                        </ul>
                    </div>,
                    { autoClose: 5000 }
                );
                return;
            }
        }
        if (onTabChange) {
            onTabChange(tab);
        }
    }

    function handleVehicleSearchInputChange(event) {
        if (onVehicleSearchChange) {
            onVehicleSearchChange(event.target.value);
        }
    }

    function resetVehicleSearch() {
        if (onVehicleSearchReset) {
            onVehicleSearchReset();
        }
    }

    const preparedServices = useMemo(() => {
        if (!Array.isArray(serviceBundles)) return [];
        return serviceBundles.map(normalizeServiceBundle).filter(Boolean);
    }, [serviceBundles]);

    const preparedParts = useMemo(() => {
        if (!Array.isArray(partList)) return [];
        return partList.map((part) => ({
            id: part?.id,
            title: part?.title || part?.name || "Untitled Part",
            price: Number(part?.price ?? part?.cost ?? 0) || 0,
            estimatedTime: part?.estimatedTime || "",
            costType: part?.costType || "",
        })).filter(Boolean);
    }, [partList]);

    const preparedBundles = useMemo(() => {
        if (!Array.isArray(serviceBundleList)) return [];
        return serviceBundleList.map((bundle) => ({
            id: bundle?.id,
            title: bundle?.title || bundle?.name || "Untitled Bundle",
            price: Number(bundle?.price ?? bundle?.cost ?? 0) || 0,
        })).filter(Boolean);
    }, [serviceBundleList]);

    const hasServices = preparedServices.length > 0;
    const hasParts = preparedParts.length > 0;
    const hasBundles = preparedBundles.length > 0;
    const canAddCustomService = Boolean(selectedVehicleId);

    function handleServiceClick(serviceId) {
        if (onServiceToggle) {
            onServiceToggle(serviceId);
        }
    }

    function handlePartClick(partId) {
        if (onPartToggle) {
            onPartToggle(partId);
        }
    }

    function handleBundleClick(bundleId) {
        if (onBundleToggle) {
            onBundleToggle(bundleId);
        }
    }

    const vehicleCount = selectedVehicle ? 1 : 0;
    const serviceCount = selectedServices.length;
    const partCount = selectedParts?.length || 0;
    const bundleCount = selectedBundles?.length || 0;
    const customServiceCount = customServices.length;
    const tireCount = tireQuantity ? 1 : 0;
    const totalServiceCount = serviceCount + partCount + bundleCount + customServiceCount + tireCount;

    const vehicleSummary = useMemo(() => buildVehicleSummary(selectedVehicle), [selectedVehicle]);
    const formattedOverallTotal = formatCurrency(overallTotal);
    const hasCustomServices = customServices.length > 0;

    // Set default values for tire bundles
    useEffect(() => {
        if (!onBundleMetadataChange) return;
        
        selectedBundles?.forEach((bundle) => {
            const isTireBundle = /tire.*mount.*and.*balance/i.test(bundle.title || "");
            if (!isTireBundle) return;
            
            const metadata = bundleMetadata[bundle.id] || {};
            
            // Set default tire brand if not already set
            if (!metadata.tireBrand) {
                onBundleMetadataChange(bundle.id, "tireBrand", "NO SPECIFIC BRAND");
            }
            
            // Set default tire change reason if not already set
            if (!metadata.tireChangeReason) {
                onBundleMetadataChange(bundle.id, "tireChangeReason", "OTHER");
            }
            
            // Set default tire type if not already set
            if (!metadata.tireType) {
                onBundleMetadataChange(bundle.id, "tireType", "All-Season");
            }
            
            // Set default tire quantity from bundle title if not already set
            const quantityMatch = bundle.title?.match(/\((\d+)\)/);
            const defaultTireQuantity = quantityMatch ? quantityMatch[1] : "";
            if (!metadata.tireQuantity && defaultTireQuantity) {
                onBundleMetadataChange(bundle.id, "tireQuantity", defaultTireQuantity);
            }
        });
    }, [selectedBundles, bundleMetadata, onBundleMetadataChange]);

    return (
        <div
            className="mb-6 shadow-lg"
            style={{
                background: "linear-gradient(135deg, #f0fff5 0%, #d7f5e3 100%)",
                borderRadius: "16px",
                padding: "16px"
            }}
        >
            <style>{`
                @media (min-width: 640px) {
                    .quotation-panel-container {
                        padding: 20px 24px !important;
                    }
                }
            `}</style>
            <div className="mb-4">
                <div className="flex gap-2 flex-wrap">
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab;
                        return (
                            <button
                                key={tab}
                                onClick={() => handleTabChange(tab)}
                                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${isActive ? "bg-green-600 text-white" : "bg-white text-green-700"}`}
                                style={{ boxShadow: isActive ? "0 6px 16px rgba(22, 163, 74, 0.35)" : "0 2px 8px rgba(21, 128, 61, 0.15)" }}
                            >
                                {tab}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div
                className="bg-white rounded-xl p-3 sm:p-6 border border-green-100"
                style={{ minHeight: "180px", boxShadow: "0 8px 20px rgba(21, 128, 61, 0.12)" }}
            >
                {activeTab === "Select Vehicle" && (
                    <div>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                            Choose a vehicle from your fleet. You can tap a vehicle below to continue.
                        </p>
                        <div className="mt-4">
                            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                                <div className="flex-1 relative">
                                    <input
                                        type="text"
                                        value={vehicleSearch}
                                        onChange={handleVehicleSearchInputChange}
                                        placeholder="Search by make, model, year or VIN..."
                                        className="w-full rounded-lg border border-green-100 pl-10 pr-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                    <svg
                                        className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                </div>
                                {isVehicleSearchActive && (
                                    <button
                                        type="button"
                                        onClick={resetVehicleSearch}
                                        className="text-sm font-semibold text-green-600 hover:text-green-700 px-3 py-2"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            {isLoading && (
                                <div className={"rounded-2xl border border-emerald-100 bg-white px-6 py-12 text-center text-emerald-600 shadow-[0_18px_40px_rgba(22,101,52,0.12)]"}>Loading vehicles...</div>
                            )}
                            {!isLoading && hasVisibleVehicles && (
                                <div className="mt-4 grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                                    {visibleVehicles.map((vehicle) => {
                                        const isSelected = selectedVehicleId === vehicle.id;
                                        const displayName = [vehicle.make, vehicle.model].filter(Boolean).join(" ") || vehicle.title;
                                        const titleWithYear = vehicle.year ? `${displayName} (${vehicle.year})` : displayName;
                                        const statusBadge = getStatusBadgeMeta(vehicle.status);
                                        const mileageDisplay = formatMileage(vehicle.mileage);
                                        return (
                                            <button
                                                key={vehicle.id}
                                                onClick={() => handleSelectVehicle(vehicle.id)}
                                                className={`h-full w-full rounded-xl sm:rounded-2xl border px-3 py-3 sm:px-5 sm:py-5 text-left transition-all ${isSelected ? "border-green-600 bg-green-50" : "border-green-100 bg-white hover:border-green-200"}`}
                                                style={{
                                                    boxShadow: isSelected ? "0 6px 18px rgba(22, 163, 74, 0.24)" : "0 2px 10px rgba(21, 128, 61, 0.12)"
                                                }}
                                            >
                                                <div className="flex h-full flex-col justify-between gap-2">
                                                    <div className="space-y-1.5 sm:space-y-2">
                                                        <div className="text-sm sm:text-base font-semibold text-slate-900 break-words">{titleWithYear}</div>
                                                        <div className="text-xs sm:text-sm text-gray-500 break-all">
                                                            Vin: {vehicle.vin || "-"}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs">
                                                        {statusBadge.label && (
                                                            <span className={`rounded-full px-2 py-0.5 text-[10px] sm:text-xs font-semibold ${statusBadge.className}`}>
                                                                {statusBadge.label}
                                                            </span>
                                                        )}
                                                        <span className="text-gray-500 text-[10px] sm:text-xs">{mileageDisplay}</span>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {!isLoading && isVehicleSearchActive && !hasVisibleVehicles && (
                                <div className={`${MUTED_TEXT_CLASS} mt-4`}>
                                    No vehicles match your search. Try adjusting your search term.
                                </div>
                            )}
                            {!isLoading && !isVehicleSearchActive && !hasVehicles && (
                                <div className={`${MUTED_TEXT_CLASS} mt-4`}>
                                    No vehicles found for your fleet yet. Add a vehicle first or refresh the panel to try again.
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {activeTab === "Select Service" && (
                    <div>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                            Pick one or more services to include in this service request. Detailes are listed for quick review.
                        </p>
                        
                        {/* Service Search Bar */}
                        <div className="mt-3 sm:mt-4">
                            <input
                                type="text"
                                value={serviceSearch}
                                onChange={(e) => setServiceSearch(e.target.value)}
                                placeholder="Search services by name..."
                                className="w-full rounded-lg border border-green-200 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                            />
                        </div>
                        
                        {/* Service List Section */}
                        <div className="mt-4 sm:mt-6 text-xs sm:text-sm font-semibold uppercase tracking-wide text-green-700">
                            Service List
                        </div>
                        <div className="mt-4 max-h-96 overflow-y-auto pr-2">
                            {(isServiceLoading || isServiceBundleListLoading) && (
                                <div className={MUTED_TEXT_CLASS}>Loading services...</div>
                            )}
                            {!isServiceLoading && !isServiceBundleListLoading && !selectedVehicleId && (
                                <div className={MUTED_TEXT_CLASS}>
                                    Select a vehicle to enable service selection.
                                </div>
                            )}
                            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                                {/* Render Services */}
                                {!isServiceLoading && selectedVehicleId && hasServices && preparedServices
                                    .filter((service) => {
                                        if (!serviceSearch.trim()) return true;
                                        const searchLower = serviceSearch.toLowerCase();
                                        return service.title?.toLowerCase().includes(searchLower);
                                    })
                                    .map((service) => {
                                    const isSelected = selectedServiceIds.includes(service.id);
                                    return (
                                        <button
                                            key={`service-${service.id}`}
                                            onClick={() => handleServiceClick(service.id)}
                                            className={`h-full w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border transition-all ${isSelected ? "border-green-600 bg-green-50" : "border-green-100 bg-white hover:border-green-300"}`}
                                            style={{ boxShadow: isSelected ? "0 6px 18px rgba(22, 163, 74, 0.24)" : "0 2px 10px rgba(21, 128, 61, 0.15)" }}
                                        >
                                            <div className="flex items-start justify-between gap-2 sm:gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs sm:text-sm font-semibold text-green-700 break-words">
                                                        {service.title}
                                                        {service.estimatedTime > 0 && (
                                                            <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs font-normal text-gray-500">(Estimated Time: {service.estimatedTime} min)</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={`text-xs sm:text-sm font-semibold flex-shrink-0 ${isSelected ? "text-green-700" : "text-gray-500"}`}>
                                                    {formatCurrency(service.price)}
                                                </div>
                                            </div>
                                            <div className={`mt-2 sm:mt-3 w-full h-2 rounded-full ${isSelected ? "bg-green-500" : "bg-green-100"}`}></div>
                                        </button>
                                    );
                                })}
                                {/* Render Bundles */}
                                {!isServiceBundleListLoading && selectedVehicleId && hasBundles && preparedBundles
                                    .filter((bundle) => {
                                        if (!serviceSearch.trim()) return true;
                                        const searchLower = serviceSearch.toLowerCase();
                                        return bundle.title?.toLowerCase().includes(searchLower);
                                    })
                                    .map((bundle) => {
                                    const isSelected = selectedBundleIds.includes(bundle.id);
                                    const isTireBundle = /tire.*mount.*and.*balance/i.test(bundle.title || "");
                                    const metadata = bundleMetadata[bundle.id] || {};
                                    
                                    // Extract tire quantity from bundle title (e.g., "Tire Mount and Balance (2)" -> "2")
                                    const quantityMatch = bundle.title?.match(/\((\d+)\)/);
                                    const defaultTireQuantity = quantityMatch ? quantityMatch[1] : "";
                                    
                                    return (
                                        <div key={`bundle-${bundle.id}`} className="h-full flex flex-col">
                                            <button
                                                onClick={() => handleBundleClick(bundle.id)}
                                                className={`w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border transition-all ${isSelected ? "border-green-600 bg-green-50" : "border-green-100 bg-white hover:border-green-300"}`}
                                                style={{ boxShadow: isSelected ? "0 6px 18px rgba(22, 163, 74, 0.24)" : "0 2px 10px rgba(21, 128, 61, 0.15)" }}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div>
                                                        <div className="text-sm font-semibold text-green-700">{bundle.title}</div>
                                                    </div>
                                                    <div className={`text-sm font-semibold ${isSelected ? "text-green-700" : "text-gray-500"}`}>
                                                        {formatCurrency(bundle.price)}
                                                </div>
                                            </div>
                                            <div className={`mt-3 w-full h-2 rounded-full ${isSelected ? "bg-green-500" : "bg-green-100"}`}></div>
                                        </button>
                                        
                                        {/* Tire-specific inputs for selected tire bundles */}
                                        {isSelected && isTireBundle && (
                                            <div className="mt-3 ml-4 p-4 rounded-lg bg-green-50 border border-green-200 space-y-3">
                                                <div className="text-sm font-semibold text-green-700 mb-2">Tire Details</div>
                                                
                                                {/* Tire Brand Input */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">* Tire Brand</label>
                                                    <select
                                                        value={metadata.tireBrand || "NO SPECIFIC BRAND"}
                                                        onChange={(e) => onBundleMetadataChange && onBundleMetadataChange(bundle.id, "tireBrand", e.target.value)}
                                                        className="w-full rounded-lg border border-green-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                                                        required
                                                    >
                                                        {tireBrands.map((brand, index) => (
                                                            <option key={index} value={brand.tireBrand}>
                                                                {brand.tireBrand}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Tire Size Input */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Tire Size</label>
                                                    <input
                                                        type="text"
                                                        value={metadata.tireSize || ""}
                                                        onChange={(e) => onBundleMetadataChange && onBundleMetadataChange(bundle.id, "tireSize", e.target.value)}
                                                        placeholder="e.g., 225/45R17"
                                                        className="w-full rounded-lg border border-green-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                                                    />
                                                </div>

                                                {/* Tire Change Reason Dropdown */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">* Reason for Tire Change</label>
                                                    <select
                                                        value={metadata.tireChangeReason || "OTHER"}
                                                        onChange={(e) => onBundleMetadataChange && onBundleMetadataChange(bundle.id, "tireChangeReason", e.target.value)}
                                                        className="w-full rounded-lg border border-green-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                                                        required
                                                    >
                                                        <option value="FLAT_REPLACE">Flat Replace</option>
                                                        <option value="WEAR_REPLACE">Excessive Wear</option>
                                                        <option value="AXLE_PAIR_REPLACE">Axle Pair Replace</option>
                                                        <option value="TEMP_SPARE">Temporary Spare</option>
                                                        <option value="SIDEWALL_REPLACE">Sidewall Damage</option>
                                                        <option value="BLOWOUT_REPLACE">Blowout</option>
                                                        <option value="PREVENTIVE_REPLACE">Preventive Replace</option>
                                                        <option value="OTHER">Other</option>
                                                    </select>
                                                </div>
                                                
                                                {/* Tire Type Dropdown */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">* Tire Type</label>
                                                    <select
                                                        value={metadata.tireType || "All-Season"}
                                                        onChange={(e) => onBundleMetadataChange && onBundleMetadataChange(bundle.id, "tireType", e.target.value)}
                                                        className="w-full rounded-lg border border-green-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                                                        required
                                                    >
                                                        <option value="All-Season">All-Season</option>
                                                        <option value="Winter">Winter</option>
                                                        <option value="Summer">Summer</option>
                                                        <option value="All-Terrain">All-Terrain</option>
                                                        <option value="Performance">Performance</option>
                                                        <option value="Run-Flats">Run-Flats</option>
                                                        <option value="Temporary Spares">Temporary Spares</option>
                                                        <option value="Trailer Tires">Trailer Tires</option>
                                                    </select>
                                                </div>
                                                
                                                {/* Tire Quantity */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Tire Quantity</label>
                                                    <select
                                                        value={metadata.tireQuantity || defaultTireQuantity}
                                                        onChange={(e) => onBundleMetadataChange && onBundleMetadataChange(bundle.id, "tireQuantity", e.target.value)}
                                                        className="w-full rounded-lg border border-green-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                                                    >
                                                        <option value="">Select quantity</option>
                                                        <option value="1">1</option>
                                                        <option value="2">2</option>
                                                        <option value="3">3</option>
                                                        <option value="4">4</option>
                                                    </select>
                                                </div>
                                                
                                                {/* Tire Notes */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                                                    <textarea
                                                        value={metadata.tireNotes || ""}
                                                        onChange={(e) => onBundleMetadataChange && onBundleMetadataChange(bundle.id, "tireNotes", e.target.value)}
                                                        placeholder="Add any notes for this tire service"
                                                        className="w-full rounded-lg border border-green-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white min-h-[60px]"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            </div>
                            {!isServiceLoading && !isServiceBundleListLoading && selectedVehicleId && !hasServices && !hasBundles && (
                                <div className={MUTED_TEXT_CLASS}>
                                    No services available right now. Try refreshing later or contact support.
                                </div>
                            )}
                        </div>

                        {/* Custom Service Section */}
                        <div className="mt-6">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className={SECTION_HEADING_CLASS}>Custom Service</div>
                                {hasCustomServices && (
                                    <div className={MUTED_TEXT_CLASS}>
                                        {customServiceCount} added
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                <input
                                    type="text"
                                    value={customServiceName}
                                    onChange={(event) => setCustomServiceName(event.target.value)}
                                    placeholder="Enter custom service name"
                                    className="flex-1 rounded-lg border border-green-100 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    disabled={!canAddCustomService}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const trimmed = customServiceName.trim();
                                        if (!trimmed || !canAddCustomService) return;
                                        if (onAddCustomService) {
                                            onAddCustomService(trimmed);
                                            setCustomServiceName("");
                                        }
                                    }}
                                    className="rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed"
                                    disabled={!canAddCustomService || !customServiceName.trim()}
                                >
                                    Add
                                </button>
                            </div>

                            {!canAddCustomService && (
                                <div className="mt-1 text-sm text-gray-500">
                                    Select a vehicle to enable custom services.
                                </div>
                            )}

                            {hasCustomServices && (
                                <ul className="mt-3 space-y-2">
                                    {customServices.map((service, index) => (
                                        <li
                                            key={service.id || `${service.itemName}-${index}`}
                                            className="flex items-center justify-between rounded-lg border border-green-100 bg-white px-4 py-2 text-sm text-gray-700"
                                        >
                                            <span className="text-sm font-medium text-green-700">{service.itemName}</span>
                                            <button
                                                type="button"
                                                onClick={() => onRemoveCustomService && onRemoveCustomService(service.id || index)}
                                                className="text-xs font-semibold text-red-500 hover:text-red-600"
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
                {activeTab === "Review and Submit" && (
                    <div className="space-y-5">
                        <div>
                            <h3 className={SECTION_HEADING_CLASS}>Selected Vehicle</h3>
                            {selectedVehicle ? (
                                <div className="mt-2 flex items-center gap-3 rounded-lg border border-green-100 bg-green-50 px-3 py-2 text-sm font-semibold text-green-800">
                                    <span>{vehicleSummary || selectedVehicle.title}</span>
                                    {selectedVehicle.subtitle && (
                                        <span className="text-sm font-normal text-gray-500">{selectedVehicle.subtitle}</span>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-2 text-sm text-gray-500">Select a vehicle on the first tab to continue.</div>
                            )}
                        </div>

                        <div>
                            <h3 className={SECTION_HEADING_CLASS}>Selected Items</h3>
                            {totalServiceCount > 0 ? (
                                <ul className="mt-2 space-y-2">
                                    {selectedServices.map((service) => (
                                        <li
                                            key={service.id}
                                            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-100 px-3 py-2 text-sm"
                                        >
                                            <div className="flex items-center gap-3 text-sm font-medium text-gray-800">
                                                <span className={`${BADGE_CLASS} bg-emerald-100 text-emerald-700`}>service</span>
                                                <span>{service.title}</span>
                                                {service.estimatedTime > 0 && (
                                                    <span className="text-xs text-gray-500">(Estimated Time: {service.estimatedTime} min)</span>
                                                )}
                                            </div>
                                            <div className="text-sm font-semibold text-green-700">
                                                {formatCurrency(service.price)}
                                            </div>
                                        </li>
                                    ))}
                                    {selectedBundles?.map((bundle) => {
                                        const metadata = bundleMetadata[bundle.id] || {};
                                        const isTireBundle = /tire.*mount.*and.*balance/i.test(bundle.title || "");
                                        
                                        // Extract tire quantity from bundle title for default value
                                        const quantityMatch = bundle.title?.match(/\((\d+)\)/);
                                        const defaultTireQuantity = quantityMatch ? quantityMatch[1] : "";
                                        
                                        // Use metadata values or defaults
                                        const displayTireType = metadata.tireType || (isTireBundle ? "All-Season" : "");
                                        const displayTireQuantity = metadata.tireQuantity || defaultTireQuantity;
                                        
                                        const hasTireDetails = metadata.tireBrand || metadata.tireSize || metadata.tireChangeReason || displayTireType || displayTireQuantity || metadata.tireNotes;
                                        
                                        return (
                                            <li
                                                key={bundle.id}
                                                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-100 px-3 py-2 text-sm"
                                            >
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 text-sm font-medium text-gray-800">
                                                        <span className={`${BADGE_CLASS} bg-green-100 text-green-700`}>bundle</span>
                                                        <span>{bundle.title}</span>
                                                    </div>
                                                    {hasTireDetails && (
                                                        <div className="mt-2 ml-16 text-xs text-gray-600 space-y-1">
                                                            {metadata.tireBrand && (
                                                                <div><span className="font-semibold">Tire Brand:</span> {metadata.tireBrand}</div>
                                                            )}
                                                            {metadata.tireSize && (
                                                                <div><span className="font-semibold">Tire Size:</span> {metadata.tireSize}</div>
                                                            )}
                                                            {metadata.tireChangeReason && (
                                                                <div><span className="font-semibold">Change Reason:</span> {metadata.tireChangeReason}</div>
                                                            )}
                                                            {displayTireType && (
                                                                <div><span className="font-semibold">Tire Type:</span> {displayTireType}</div>
                                                            )}
                                                            {displayTireQuantity && (
                                                                <div><span className="font-semibold">Quantity:</span> {displayTireQuantity}</div>
                                                            )}
                                                            {metadata.tireNotes && (
                                                                <div><span className="font-semibold">Notes:</span> {metadata.tireNotes}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-sm font-semibold text-green-700">
                                                    {formatCurrency(bundle.price)}
                                                </div>
                                            </li>
                                        );
                                    })}
                                    {selectedParts?.map((part) => (
                                        <li
                                            key={part.id}
                                            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-100 px-3 py-2 text-sm"
                                        >
                                            <div className="flex items-center gap-3 text-sm font-medium text-gray-800">
                                                <span className={`${BADGE_CLASS} bg-amber-100 text-amber-700`}>part</span>
                                                <span>{part.title}</span>
                                                {part.estimatedTime > 0 && (
                                                    <span className="text-xs text-gray-500">(Estimated Time: {part.estimatedTime} min)</span>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-semibold text-amber-700">
                                                    {formatCurrency(part.price)}
                                                </div>
                                                {part.labourCost > 0 && (
                                                    <div className="text-xs text-gray-400">
                                                        {formatCurrency(part.baseCost)} + {formatCurrency(part.labourCost)}
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                    {customServices.map((service, index) => (
                                        <li
                                            key={service.id || `${service.itemName}-${index}`}
                                            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-purple-100 px-3 py-2 text-sm"
                                        >
                                            <div className="flex items-center gap-3 text-sm font-medium text-gray-800">
                                                <span className={`${BADGE_CLASS} bg-purple-100 text-purple-700`}>custom</span>
                                                <span>{service.itemName}</span>
                                            </div>
                                            <div className="text-sm font-semibold text-purple-700">-</div>
                                        </li>
                                    ))}
                                    {tireQuantity && (
                                        <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-violet-100 px-3 py-2 text-sm">
                                            <div className="flex items-center gap-3 text-sm font-medium text-gray-800">
                                                <span className={`${BADGE_CLASS} bg-violet-100 text-violet-700`}>tire</span>
                                                <span>Tire x {tireQuantity}</span>
                                                {tireNotes && <span className="text-xs text-gray-500">({tireNotes})</span>}
                                            </div>
                                            <div className="text-sm font-semibold text-violet-700">-</div>
                                        </li>
                                    )}
                                </ul>
                            ) : (
                                <div className="mt-2 text-sm text-gray-500">Select at least one service, bundle, part, or add a custom service in the second tab.</div>
                            )}
                        </div>

                        <div>
                            <h3 className={SECTION_HEADING_CLASS}>Additional Notes</h3>
                            <textarea
                                value={notes}
                                onChange={(event) => onNotesChange && onNotesChange(event.target.value)}
                                className="mt-2 w-full min-h-[90px] rounded-lg border border-green-100 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                placeholder="Add any context or special instructions for this service request."
                            />
                        </div>

                        <div className="flex flex-col gap-3 rounded-lg border border-green-100 bg-green-50 p-4">
                            <div className="flex items-center justify-between">
                                <div className={MUTED_TEXT_CLASS}>
                                    {`${vehicleCount} vehicle${vehicleCount === 1 ? "" : "s"}`}
                                    {serviceCount > 0 ? ` • ${serviceCount} service${serviceCount === 1 ? "" : "s"}` : ""}
                                    {bundleCount > 0 ? ` • ${bundleCount} bundle${bundleCount === 1 ? "" : "s"}` : ""}
                                    {partCount > 0 ? ` • ${partCount} part${partCount === 1 ? "" : "s"}` : ""}
                                    {customServiceCount > 0 ? ` • ${customServiceCount} custom` : ""}
                                    {tireCount > 0 ? ` • ${tireCount} tire` : ""}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 text-sm border-t border-green-200 pt-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Amount</span>
                                    <span className="font-semibold text-gray-700">
                                        {customServiceCount > 0 && (
                                            <span className="ml-1 text-xs font-normal text-gray-500">custom service + </span>
                                        )}
                                        {formatCurrency(subtotal)}
                                    </span>
                                </div>
                                {taxRate > 0 && partsBaseCostTotal > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Tax ({taxRate}%)</span>
                                        <span className="font-semibold text-gray-700">{formatCurrency(taxAmount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between border-t border-green-200 pt-2 mt-1">
                                    <span className="text-gray-700 font-semibold">Final Amount</span>
                                    <span className="text-base font-bold text-green-700">{formattedOverallTotal}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={isMaintenance}
                                    onChange={(e) => onIsMaintenanceChange && onIsMaintenanceChange(e.target.checked)}
                                    className="w-4 h-4 rounded border-green-300 text-green-600 focus:ring-green-500 focus:ring-2 cursor-pointer"
                                />
                                <span className="text-sm font-medium text-gray-700">Is Maintenance</span>
                            </label>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={() => onCancel && onCancel()}
                                    className="px-5 py-2 rounded-full border border-green-600 text-green-700 font-semibold hover:bg-green-50 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onSubmit && onSubmit()}
                                    className={`px-5 py-2 rounded-full font-semibold text-white transition-colors ${totalServiceCount > 0 && selectedVehicle && !isSubmitting
                                        ? "bg-green-600 hover:bg-green-700"
                                        : "bg-green-300 cursor-not-allowed"
                                        }`}
                                    disabled={!selectedVehicle || totalServiceCount === 0 || isSubmitting}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default QuotationRequestPanel;
